import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Tabs,
  Tab,
  Paper,
  Checkbox,
  RadioGroup,
  FormControlLabel,
  Radio,
  Avatar,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ImageIcon from "@mui/icons-material/Image";
import externalLinksApiService, {
  ExternalLink,
} from "../../services/external-links-api";
import {
  parseExternalLinkDescription,
  formatExternalLinkDescription,
  getInitialLetter,
  isValidUrl,
  isValidBase64Image,
  ExternalLinkLogo,
} from "../../utils/externalLinkLogoHelper";

interface ExternalLinksCardProps {
  onSave?: () => void;
  hideHeader?: boolean;
  shouldLoad?: boolean;
}

export interface ExternalLinksCardRef {
  openAddDialog: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`external-links-tabpanel-${index}`}
      aria-labelledby={`external-links-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const getGroups = (t: any) => [
  t("config.groupServices"),
  t("config.groupDevelopment"),
  t("config.groupManagement"),
];

const ExternalLinksCard = forwardRef<
  ExternalLinksCardRef,
  ExternalLinksCardProps
>(({ onSave, hideHeader = false, shouldLoad = false }, ref) => {
  const { t } = useTranslation();
  const GROUPS = getGroups(t);
  const [links, setLinks] = useState<ExternalLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ExternalLink>({
    group: GROUPS[0],
    title: "",
    description: "",
    link: "",
    displayOrder: 0,
    isActive: true,
  });

  // Logo-related state
  const [logoType, setLogoType] = useState<"none" | "url" | "base64">("none");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoBase64, setLogoBase64] = useState("");
  const [actualDescription, setActualDescription] = useState("");
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (shouldLoad) {
      loadLinks();
    }
  }, [shouldLoad]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    openAddDialog: () => handleOpenDialog(),
  }));

  const loadLinks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await externalLinksApiService.getAllExternalLinks();
      setLinks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("config.errorLinkLoad"));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleImageError = (linkId: string) => {
    setImageErrors((prev) => new Set(prev).add(linkId));
  };

  const handleOpenDialog = (link?: ExternalLink) => {
    if (link) {
      setEditingId(link.id || null);
      setFormData(link);

      // Parse logo and description
      const { logo, description } = parseExternalLinkDescription(
        link.description,
      );
      setActualDescription(description);

      if (logo) {
        setLogoType(logo.type);
        if (logo.type === "url") {
          setLogoUrl(logo.data);
          setLogoBase64("");
        } else if (logo.type === "base64") {
          setLogoBase64(logo.data);
          setLogoUrl("");
        }
      } else {
        setLogoType("none");
        setLogoUrl("");
        setLogoBase64("");
      }
    } else {
      setEditingId(null);
      setFormData({
        group: GROUPS[tabValue],
        title: "",
        description: "",
        link: "",
        displayOrder: 0,
        isActive: true,
      });
      setLogoType("none");
      setLogoUrl("");
      setLogoBase64("");
      setActualDescription("");
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData({
      group: GROUPS[0],
      title: "",
      description: "",
      link: "",
      displayOrder: 0,
      isActive: true,
    });
    setLogoType("none");
    setLogoUrl("");
    setLogoBase64("");
    setActualDescription("");
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError(t("config.invalidImageFile"));
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError(t("config.imageTooLarge"));
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      setLogoBase64(base64);
      setError(null);
    };
    reader.onerror = () => {
      setError(t("config.errorReadingFile"));
    };
    reader.readAsDataURL(file);
  };

  const handleSaveLink = async () => {
    if (!formData.title.trim() || !formData.link.trim()) {
      setError(t("config.linkRequired"));
      return;
    }

    // Validate logo inputs
    if (logoType === "url" && logoUrl.trim() && !isValidUrl(logoUrl.trim())) {
      setError(t("config.invalidLogoUrl"));
      return;
    }
    if (
      logoType === "base64" &&
      logoBase64.trim() &&
      !isValidBase64Image(logoBase64.trim())
    ) {
      setError(t("config.invalidLogoBase64"));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Prepare logo data
      let logo: ExternalLinkLogo | null = null;
      if (logoType === "url" && logoUrl.trim()) {
        logo = { type: "url", data: logoUrl.trim() };
      } else if (logoType === "base64" && logoBase64.trim()) {
        logo = { type: "base64", data: logoBase64.trim() };
      }

      // Format description field with logo data
      const formattedDescription = formatExternalLinkDescription(
        logo,
        actualDescription,
      );

      // Prepare data without timestamp fields (they are managed by backend)
      const dataToSend = {
        group: formData.group,
        title: formData.title,
        description: formattedDescription,
        link: formData.link,
        displayOrder: formData.displayOrder,
        isActive: formData.isActive,
      };

      if (editingId) {
        await externalLinksApiService.updateExternalLink(
          editingId,
          dataToSend as ExternalLink,
        );
        setSuccess(t("config.linkUpdated"));
      } else {
        await externalLinksApiService.createExternalLink(
          dataToSend as ExternalLink,
        );
        setSuccess(t("config.linkCreated"));
      }

      handleCloseDialog();
      await loadLinks();
      onSave?.();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("config.errorLink"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLink = async (id: string) => {
    if (!window.confirm(t("config.deleteLinkConfirm"))) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await externalLinksApiService.deleteExternalLink(id);
      setSuccess(t("config.linkDeleted"));
      await loadLinks();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("config.errorLinkDelete"),
      );
    } finally {
      setLoading(false);
    }
  };

  const currentGroupLinks = links.filter(
    (link) => link.group === GROUPS[tabValue],
  );

  return (
    <Card sx={{ boxShadow: hideHeader ? "none" : undefined }}>
      <CardContent sx={{ p: hideHeader ? 0 : undefined }}>
        <Stack spacing={2}>
          {!hideHeader && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">{t("config.externalLinks")}</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                size="small"
              >
                {t("common.add")}
              </Button>
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          {loading && !links.length ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="external links groups"
              >
                {GROUPS.map((group, index) => (
                  <Tab
                    key={group}
                    label={group}
                    id={`external-links-tab-${index}`}
                  />
                ))}
              </Tabs>

              {GROUPS.map((group, index) => (
                <TabPanel key={group} value={tabValue} index={index}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "action.hover" }}>
                          <TableCell sx={{ fontWeight: 600, width: 60 }}>
                            {t("config.labelLogo")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t("config.title")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t("config.description")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t("config.link")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, width: 100 }}>
                            {t("common.actions")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentGroupLinks.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              align="center"
                              sx={{ py: 3 }}
                            >
                              <Typography color="text.secondary">
                                {t("config.noLinks")}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          currentGroupLinks.map((link) => {
                            // Parse logo and description
                            const { logo, description } =
                              parseExternalLinkDescription(link.description);
                            const linkId = link.id?.toString() || "";
                            const hasImageError = imageErrors.has(linkId);
                            const shouldShowImage = logo && !hasImageError;

                            return (
                              <TableRow key={link.id} hover>
                                <TableCell>
                                  <Avatar
                                    src={
                                      shouldShowImage
                                        ? logo.type === "url"
                                          ? logo.data
                                          : logo.data
                                        : undefined
                                    }
                                    imgProps={{
                                      onError: () => handleImageError(linkId),
                                    }}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: shouldShowImage
                                        ? "transparent"
                                        : "primary.main",
                                      fontSize: "0.9rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {!shouldShowImage &&
                                      getInitialLetter(link.title)}
                                  </Avatar>
                                </TableCell>
                                <TableCell>{link.title}</TableCell>
                                <TableCell>{description || "-"}</TableCell>
                                <TableCell>
                                  <Tooltip title={link.link}>
                                    <Box
                                      sx={{
                                        maxWidth: 200,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        cursor: "pointer",
                                        color: "primary.main",
                                        textDecoration: "underline",
                                      }}
                                      onClick={() =>
                                        window.open(link.link, "_blank")
                                      }
                                    >
                                      {link.link}
                                    </Box>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title={t("config.tooltipOpen")}>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        window.open(link.link, "_blank")
                                      }
                                    >
                                      <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t("config.tooltipEdit")}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenDialog(link)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t("config.tooltipDelete")}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteLink(link.id!)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>
              ))}
            </>
          )}
        </Stack>
      </CardContent>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingId
            ? t("common.edit") + " " + t("config.externalLinks")
            : t("common.add") + " " + t("config.externalLinks")}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ pt: 2 }}>
            <TextField
              label={t("config.labelTitle")}
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              fullWidth
              size="small"
            />

            {/* Logo Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t("config.labelLogo")} ({t("config.optional")})
              </Typography>

              {/* Logo Preview */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <Avatar
                  src={
                    logoType === "url" && logoUrl
                      ? logoUrl
                      : logoType === "base64" && logoBase64
                        ? logoBase64
                        : undefined
                  }
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor:
                      (logoType === "url" && logoUrl) ||
                      (logoType === "base64" && logoBase64)
                        ? "transparent"
                        : "primary.main",
                    fontSize: "1.5rem",
                    fontWeight: 600,
                  }}
                >
                  {(!logoType || logoType === "none") && formData.title ? (
                    getInitialLetter(formData.title)
                  ) : (
                    <ImageIcon />
                  )}
                </Avatar>
                <Typography variant="caption" color="text.secondary">
                  {t("config.logoPreview")}
                </Typography>
              </Box>

              <RadioGroup
                value={logoType}
                onChange={(e) =>
                  setLogoType(e.target.value as "none" | "url" | "base64")
                }
              >
                <FormControlLabel
                  value="none"
                  control={<Radio size="small" />}
                  label={t("config.noLogo")}
                />
                <FormControlLabel
                  value="url"
                  control={<Radio size="small" />}
                  label={t("config.logoUrl")}
                />
                <FormControlLabel
                  value="base64"
                  control={<Radio size="small" />}
                  label={t("config.logoUpload")}
                />
              </RadioGroup>

              {logoType === "url" && (
                <TextField
                  label={t("config.labelLogoUrl")}
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="https://example.com/logo.png"
                  sx={{ mt: 1 }}
                  helperText={t("config.logoUrlHelper")}
                />
              )}

              {logoType === "base64" && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<ImageIcon />}
                    size="small"
                  >
                    {t("config.uploadImage")}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleFileUpload}
                    />
                  </Button>
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {t("config.imageUploadHelper")}
                  </Typography>

                  {/* Base64 Textarea */}
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{ mt: 2, mb: 0.5 }}
                  >
                    {t("config.orPasteBase64")}
                  </Typography>
                  <TextField
                    value={logoBase64}
                    onChange={(e) => setLogoBase64(e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    placeholder="data:image/png;base64,iVBORw0KGgo..."
                    helperText={t("config.base64Helper")}
                  />

                  {logoBase64 && (
                    <Typography
                      variant="caption"
                      color="success.main"
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      ✓ {t("config.imageUploaded")}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            <TextField
              label={t("config.labelDescription")}
              value={actualDescription}
              onChange={(e) => setActualDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              size="small"
            />
            <TextField
              label={t("config.labelLink")}
              value={formData.link}
              onChange={(e) =>
                setFormData({ ...formData, link: e.target.value })
              }
              fullWidth
              size="small"
              type="url"
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Checkbox
                checked={formData.isActive || false}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
              />
              <Typography>{t("config.labelActive")}</Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("common.cancel")}</Button>
          <Button
            onClick={handleSaveLink}
            variant="contained"
            disabled={loading}
          >
            {loading ? t("common.saving") : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});

ExternalLinksCard.displayName = "ExternalLinksCard";

export default React.memo(ExternalLinksCard);
